import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Divider,
} from '@mui/material';
import type { Product, UpdateProductRequest } from '../../../types/product';
import ProductImageManager from './ProductImageManager';
import { MIN_SEARCH_LENGTH } from '../../../utils/searchConstants';
import ProductCodeField from './ProductCodeField';

interface EditProductProps {
  open: boolean;
  product: Product | null;
  onClose: () => void;
  onSubmit: (id: number, productData: UpdateProductRequest) => Promise<void>;
}

export default function EditProduct({
  open,
  product,
  onClose,
  onSubmit,
}: EditProductProps) {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (product) {
      setName(product.name);
      setCode(product.code || '');
    }
  }, [product]);

  const handleTransitionEnd = () => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!product) return;

    if (!name.trim()) {
      setError('Назва продукту обов\'язкова');
      return;
    }

    if (name.trim().length < MIN_SEARCH_LENGTH) {
      setError(`Назва продукту має містити щонайменше ${MIN_SEARCH_LENGTH} символів`);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await onSubmit(product.id, {
        name: name.trim(),
        code: code.trim() || undefined,
      });
      
      onClose();
    } catch (err) {
      console.error('Error updating product:', err);
      setError('Не вдалося оновити продукт. Спробуйте ще раз.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        transition: {
          onEntered: handleTransitionEnd,
        }
      }}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>Редагувати продукт</DialogTitle>
        
        <DialogContent>
          <TextField
            autoFocus
            inputRef={inputRef}
            label="Назва продукту"
            type="text"
            fullWidth
            variant="outlined"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            error={!!error && !name.trim()}
            helperText={error && !name.trim() ? 'Назва обов\'язкова' : ''}
            margin="normal"
          />
          
          <ProductCodeField
            label="Код продукту (необов'язково)"
            value={code}
            onChange={setCode}
            disabled={loading}
          />

          {error && (
            <div style={{ color: 'red', marginTop: '10px' }}>
              {error}
            </div>
          )}

          <Divider sx={{ my: 2 }} />
          
          {product && <ProductImageManager productId={product.id} />}
        </DialogContent>
        
        <DialogActions>
          <Button 
            onClick={handleClose} 
            disabled={loading}
            variant="outlined"
          >
            Скасувати
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={loading}
          >
            {loading ? 'Збереження...' : 'Зберегти зміни'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
